import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api-quali5d-prod-vvv.azurewebsites.net';

export default function InterviewResponsePage() {
  const { interviewId } = useParams();
  const [searchParams] = useSearchParams();

  const action = searchParams.get('action');      // accepted | declined | reschedule
  const passcode = searchParams.get('passcode');

  const [status, setStatus] = useState('idle');   // idle | submitting | done | error
  const [message, setMessage] = useState('');
  const [proposedDatetime, setProposedDatetime] = useState('');
  const [showRescheduleForm, setShowRescheduleForm] = useState(false);

  // Auto-submit for accept/decline; show form for reschedule
  useEffect(() => {
    if (!action || !interviewId) return;
    if (action === 'reschedule') {
      setShowRescheduleForm(true);
    } else if (action === 'accepted' || action === 'declined') {
      submitResponse(action, null);
    }
    // eslint-disable-next-line
  }, []);

  const submitResponse = async (responseAction, proposed) => {
    setStatus('submitting');
    try {
      const body = { action: responseAction };
      if (proposed) body.proposed_datetime = proposed;
      if (passcode) body.passcode = passcode;

      const res = await fetch(`${API_BASE}/api/phase3/interview/${interviewId}/invite-response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        setStatus('done');
        if (responseAction === 'accepted') setMessage('accepted');
        else if (responseAction === 'declined') setMessage('declined');
        else setMessage('rescheduled');
      } else {
        const err = await res.json().catch(() => ({}));
        if (res.status === 400 && err.detail?.includes('already')) {
          setStatus('done');
          setMessage('already_responded');
        } else {
          setStatus('error');
          setMessage(err.detail || 'Something went wrong. Please try again.');
        }
      }
    } catch (e) {
      setStatus('error');
      setMessage('Could not connect to the server. Please try again later.');
    }
  };

  const handleRescheduleSubmit = () => {
    if (!proposedDatetime) return;
    submitResponse('rescheduled', new Date(proposedDatetime).toISOString());
  };

  // ── UI States ──

  if (status === 'submitting') {
    return <Layout><Spinner text="Processing your response..." /></Layout>;
  }

  if (status === 'done') {
    const configs = {
      accepted: {
        icon: '✅',
        title: 'Interview Accepted!',
        subtitle: 'You\'re all set. You\'ll receive a confirmation shortly.',
        color: '#16a34a',
        bg: '#f0fdf4',
        border: '#86efac',
      },
      declined: {
        icon: '❌',
        title: 'Interview Declined',
        subtitle: 'We\'ve recorded your response. The recruiter will be notified.',
        color: '#dc2626',
        bg: '#fef2f2',
        border: '#fca5a5',
      },
      rescheduled: {
        icon: '📅',
        title: 'Reschedule Requested',
        subtitle: 'Your proposed time has been sent to the recruiter. They\'ll confirm shortly.',
        color: '#d97706',
        bg: '#fffbeb',
        border: '#fcd34d',
      },
      already_responded: {
        icon: 'ℹ️',
        title: 'Already Responded',
        subtitle: 'You have already responded to this interview invitation.',
        color: '#2563eb',
        bg: '#eff6ff',
        border: '#93c5fd',
      },
    };

    const cfg = configs[message] || configs.already_responded;

    return (
      <Layout>
        <div style={{
          background: cfg.bg,
          border: `2px solid ${cfg.border}`,
          borderRadius: 16,
          padding: 40,
          textAlign: 'center',
          maxWidth: 480,
          margin: '0 auto',
        }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>{cfg.icon}</div>
          <h2 style={{ color: cfg.color, margin: '0 0 12px', fontSize: 24, fontWeight: 700 }}>
            {cfg.title}
          </h2>
          <p style={{ color: '#6b7280', margin: 0, fontSize: 15, lineHeight: 1.6 }}>
            {cfg.subtitle}
          </p>
        </div>
        <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12, marginTop: 32 }}>
          Powered by <strong>Quali5d</strong> — AI-Powered Recruitment Intelligence
        </p>
      </Layout>
    );
  }

  if (status === 'error') {
    return (
      <Layout>
        <div style={{
          background: '#fef2f2', border: '2px solid #fca5a5',
          borderRadius: 16, padding: 40, textAlign: 'center', maxWidth: 480, margin: '0 auto',
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <h2 style={{ color: '#dc2626', margin: '0 0 12px' }}>Something went wrong</h2>
          <p style={{ color: '#6b7280', margin: '0 0 24px' }}>{message}</p>
          <button
            onClick={() => { setStatus('idle'); setShowRescheduleForm(action === 'reschedule'); }}
            style={{
              background: '#1a56db', color: 'white', border: 'none',
              borderRadius: 8, padding: '10px 24px', cursor: 'pointer', fontSize: 14,
            }}
          >
            Try Again
          </button>
        </div>
      </Layout>
    );
  }

  // Reschedule form
  if (showRescheduleForm) {
    return (
      <Layout>
        <div style={{
          background: 'white', border: '1px solid #e5e7eb',
          borderRadius: 16, padding: 40, maxWidth: 480, margin: '0 auto',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📅</div>
            <h2 style={{ color: '#111827', margin: '0 0 8px', fontSize: 22, fontWeight: 700 }}>
              Request a Reschedule
            </h2>
            <p style={{ color: '#6b7280', margin: 0, fontSize: 14 }}>
              Choose a time that works better for you
            </p>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', color: '#374151', fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
              Preferred Date & Time
            </label>
            <input
              type="datetime-local"
              value={proposedDatetime}
              onChange={e => setProposedDatetime(e.target.value)}
              min={new Date(Date.now() + 3600000).toISOString().slice(0, 16)}
              style={{
                width: '100%', padding: '10px 14px', border: '1px solid #d1d5db',
                borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
                outline: 'none', color: '#111827',
              }}
            />
          </div>

          <button
            onClick={handleRescheduleSubmit}
            disabled={!proposedDatetime}
            style={{
              width: '100%', background: proposedDatetime ? '#d97706' : '#9ca3af',
              color: 'white', border: 'none', borderRadius: 8, padding: '12px',
              fontSize: 15, fontWeight: 700, cursor: proposedDatetime ? 'pointer' : 'not-allowed',
            }}
          >
            Send Reschedule Request
          </button>
        </div>
        <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12, marginTop: 32 }}>
          Powered by <strong>Quali5d</strong> — AI-Powered Recruitment Intelligence
        </p>
      </Layout>
    );
  }

  return <Layout><Spinner text="Loading..." /></Layout>;
}

function Layout({ children }) {
  return (
    <div style={{
      minHeight: '100vh', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: 24, fontFamily: 'Arial, sans-serif',
    }}>
      <div style={{ marginBottom: 32, textAlign: 'center' }}>
        <h1 style={{
          background: 'linear-gradient(135deg, #1a56db, #0ea5e9)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          fontSize: 28, fontWeight: 800, margin: 0,
        }}>
          Quali5d
        </h1>
        <p style={{ color: '#6b7280', fontSize: 13, margin: '4px 0 0' }}>
          AI-Powered Recruitment Intelligence
        </p>
      </div>
      {children}
    </div>
  );
}

function Spinner({ text }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: 48, height: 48, border: '4px solid #e5e7eb',
        borderTop: '4px solid #1a56db', borderRadius: '50%',
        animation: 'spin 1s linear infinite', margin: '0 auto 16px',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <p style={{ color: '#6b7280', fontSize: 14 }}>{text}</p>
    </div>
  );
}